import { ComputedRef, Ref } from 'vue'

import { DailySoluition } from '@/domain/entities/dashboard/dailySolution'
import { User } from '@/domain/entities/user/user'
import { DisplayLanguageType } from '@/domain/value_objects/language/displayLanguageType'
import { DailyEffortsViewModel } from '@/presentation/viewmodels/dashboard/dailyEffortsViewModel'
import { StatisticsViewModel } from '@/presentation/viewmodels/dashboard/statisticsViewModel'
import { getLineChartOptions } from '@/util/dashboard/lineChartOptions'

export class DailyEffortsPresenter {
  private viewModel: DailyEffortsViewModel
  private statisticsViewModel: StatisticsViewModel

  constructor(
    statisticsViewModel: StatisticsViewModel,
    viewModel: DailyEffortsViewModel = new DailyEffortsViewModel()
  ) {
    this.viewModel = viewModel
    this.statisticsViewModel = statisticsViewModel
  }

  get chartData(): Ref<{ labelChartData: number[]; dailyEffortsChartData: number[][]; maxEffort: number }> {
    return this.viewModel.chartData
  }

  get chartSeries(): Ref<any[]> {
    return this.viewModel.chartSeries
  }

  get chartOptions(): Ref<any> {
    return this.viewModel.chartOptions
  }

  get hasData(): Ref<boolean> {
    return this.viewModel.hasData
  }

  get isLoading(): ComputedRef<boolean> {
    return this.statisticsViewModel.isLoading
  }

  get error(): ComputedRef<string | null> {
    return this.statisticsViewModel.error
  }

  initChart = (
    userList: User[],
    color: string,
    otherColor: string,
    displayLanguage: DisplayLanguageType,
    isLightMode: boolean
  ) => {
    const chartData = this.getChartOfDailyEffortData()

    if (chartData.labelChartData.length == 0) {
      this.viewModel.setHasData(false)
      return
    }

    try {
      const chartSeries = []
      for (let i = 0; i < userList.length; i++) {
        chartSeries.push(
          this.getChartData(userList[i].id, chartData.dailyEffortsChartData[i], i == 0 ? color : otherColor)
        )
      }
      const chartOptions = getLineChartOptions(
        displayLanguage == 'Japanese' ? '日々の解答数' : 'Daily Efforts',
        color,
        chartData.labelChartData,
        isLightMode,
        true
      )
      this.viewModel.setChartData(chartData)
      this.viewModel.setChartSeries(chartSeries)
      this.viewModel.setChartOptions(chartOptions)
      this.viewModel.setHasData(true)
    } catch (e) {
      this.viewModel.setHasData(false)
    }
  }

  private getChartData = (name: string, values: any, color: string) => {
    return {
      name: name,
      data: values,
      color: color
    }
  }

  // チャート用のデータを取得
  getChartOfDailyEffortData = () => {
    const userDaily = this.statisticsViewModel.userDailySolutionList.value

    const labelChartData = this.getDateList(userDaily)
    const dailyEffortsChartData: number[][] = []
    let maxEffort = 0

    for (let i = 0; i < userDaily.length; i++) {
      const daily = this.getUserEffortData(userDaily[i], labelChartData)
      dailyEffortsChartData.push(daily)

      if (daily.length == 0) dailyEffortsChartData[i].push(0)

      maxEffort = Math.max(
        maxEffort,
        daily.map(v => v).reduce((a, b) => Math.max(a, b))
      )
    }

    return { labelChartData, dailyEffortsChartData, maxEffort }
  }

  //   // 特定のユーザーのデータを取得
  getUserEffortData = (daily: DailySoluition[], labelChartData: number[]) => {
    const weeklyRatingChartData: number[] = []

    let dailyIndex = 0
    for (let i = 0; i < labelChartData.length; i++) {
      if (dailyIndex >= daily.length) {
        weeklyRatingChartData.push(0)
      } else if (daily[dailyIndex].date == labelChartData[i]) {
        weeklyRatingChartData.push(daily[dailyIndex].value)
        dailyIndex++
      } else {
        weeklyRatingChartData.push(0)
      }
    }

    return weeklyRatingChartData
  }

  // 不足した日付を埋める
  getDateList = (daily: DailySoluition[][]) => {
    const set = new Set<number>([])

    for (let i = 0; i < daily.length; i++) {
      for (let j = 0; j < daily[i].length; j++) {
        set.add(daily[i][j].date)
      }
    }

    const list = Array.from(set)
    list.sort()

    return list
  }
}
